import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "@/lib/firestore-instrumented";
import { buildEntitySlug } from "@/lib/chronicles";

export async function resolvePrimaryLocationSelection({
  db,
  userId,
  hubSlug,
  hubDocId,
  selection,
  existingEntities = [],
}) {
  const mode = String(selection?.mode || "none");
  if (mode === "none") return null;

  if (mode === "existing") {
    const selectedId = String(selection?.entityId || "").trim();
    if (!selectedId) throw new Error("Choose a primary location.");
    const fromList = existingEntities.find((item) => item.id === selectedId);
    if (fromList?.displayName) {
      return { entityId: selectedId, displayName: fromList.displayName };
    }
    const snap = await getDoc(doc(db, "entities", selectedId));
    const data = snap.exists() ? snap.data() : null;
    const displayName = String(data?.displayName || "").trim();
    if (!displayName) throw new Error("Primary location could not be resolved.");
    return { entityId: selectedId, displayName };
  }

  if (mode === "create") {
    const displayName = String(selection?.entity?.displayName || "").trim();
    if (!displayName) throw new Error("Primary location name is required.");

    const latitudeValue = selection?.entity?.typed?.latitude;
    const longitudeValue = selection?.entity?.typed?.longitude;
    if (latitudeValue !== null && latitudeValue !== undefined && latitudeValue !== "" && Number.isNaN(Number(latitudeValue))) {
      throw new Error("Latitude must be a number.");
    }
    if (longitudeValue !== null && longitudeValue !== undefined && longitudeValue !== "" && Number.isNaN(Number(longitudeValue))) {
      throw new Error("Longitude must be a number.");
    }

    const entityRef = doc(collection(db, "entities"));
    await setDoc(entityRef, {
      type: "location",
      schemaVersion: 1,
      hubSlug,
      hubDocId: hubDocId || null,
      displayName,
      displayNameLower: displayName.toLowerCase(),
      slug: `${buildEntitySlug(displayName, "location")}-${entityRef.id.slice(0, 6)}`,
      aliases: selection?.entity?.aliases || [],
      summary: selection?.entity?.summary || "",
      tags: [],
      status: "active",
      externalRefs: {
        googlePlaceId: selection?.entity?.typed?.placeId || null,
        duns: null,
        wikidata: null,
        voterRegistryKey: null,
        whitepagesPersonId: null,
      },
      createdBy: userId,
      updatedBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      typed: {
        locationKind: selection?.entity?.typed?.locationKind || "other",
        address1: "",
        address2: "",
        city: selection?.entity?.typed?.city || "",
        state: selection?.entity?.typed?.state || "",
        postalCode: "",
        country: selection?.entity?.typed?.country || "",
        latitude:
          selection?.entity?.typed?.latitude === "" || selection?.entity?.typed?.latitude === undefined
            ? null
            : Number(selection?.entity?.typed?.latitude),
        longitude:
          selection?.entity?.typed?.longitude === "" || selection?.entity?.typed?.longitude === undefined
            ? null
            : Number(selection?.entity?.typed?.longitude),
        placeId: selection?.entity?.typed?.placeId || null,
        geohash: null,
        formattedAddress: selection?.entity?.typed?.formattedAddress || displayName,
      },
    });
    return { entityId: entityRef.id, displayName };
  }

  return null;
}

export async function syncIncidentPrimaryLocation({
  db,
  chronicleId,
  incidentId,
  userId,
  nextPrimary = null,
}) {
  const linksCol = collection(db, "chronicles", chronicleId, "incidentEntities");
  const snap = await getDocs(query(linksCol, where("incidentId", "==", incidentId), limit(100)));
  const locationLinks = snap.docs
    .map((item) => ({ id: item.id, ref: item.ref, ...item.data() }))
    .filter((item) => String(item.entityType || "").toLowerCase() === "location");

  for (const link of locationLinks) {
    if (link.entityId !== nextPrimary?.entityId && link.role === "primary_location") {
      await updateDoc(link.ref, {
        role: "location",
        updatedAt: serverTimestamp(),
      });
    }
  }

  if (!nextPrimary?.entityId) return;

  const target = locationLinks.find((item) => item.entityId === nextPrimary.entityId);
  if (target) {
    await updateDoc(target.ref, {
      role: "primary_location",
      relevance: "primary",
      updatedAt: serverTimestamp(),
    });
    return;
  }

  const linkRef = doc(linksCol, `${incidentId}__${nextPrimary.entityId}`);
  await setDoc(linkRef, {
    incidentId,
    entityId: nextPrimary.entityId,
    entityType: "location",
    role: "primary_location",
    relevance: "primary",
    notes: "",
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
